import React, { useState, useContext, useMemo, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityOperationBar from "../../components/EntityOperationBar";
import "../../starek.css";
import DataTable from "react-data-table-component";
import moment from "moment";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import WebhookSidebar, { DELETE_ONE_WEBHOOK } from "./WebhookSidebar";
import WebhooksCardView, { WebhooksEmptyState } from "./WebhooksCardView";

const GET_WEBHOOKS_LIST = gql`
    query GetWebhooks($plantId: Int!) {
        webhooks(where: { plant_id: { equals: $plantId } }) {
            id
            name
            description
            url
            method
            parameters
            is_active
            created_at
        }
    }
`;

const Webhooks = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: urlId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState("card");

    useEffect(() => {
        const viewParam = searchParams.get("view");
        if (viewParam === "card" || viewParam === "list") setViewMode(viewParam);
    }, [searchParams]);

    const { data, loading, error, refetch } = useQuery(GET_WEBHOOKS_LIST, {
        client: starekContext.engineClient,
        variables: { plantId: starekContext.lastUsedPlant.id },
        fetchPolicy: "network-only",
    });

    const [deleteOneWebhook] = useMutation(DELETE_ONE_WEBHOOK, {
        client: starekContext.engineClient,
    });

    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteDone, setDeleteDone] = useState(0);
    const [toggleCleared, setToggleCleared] = useState(false);

    const [showSidebar, setShowSidebar] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    const webhooks = data?.webhooks ?? [];

    // Open sidebar when navigating to /webhooks/:id
    useEffect(() => {
        if (urlId != null) {
            const id = Number(urlId);
            if (id === 0) {
                setEditingItem(null);
                setShowSidebar(true);
            } else if (!loading && webhooks.length > 0) {
                const found = webhooks.find((w) => w.id === id);
                setEditingItem(found ?? { id });
                setShowSidebar(true);
            }
        }
    }, [urlId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return webhooks;
        return webhooks.filter(
            (item) =>
                item.name?.toLowerCase().includes(q) ||
                item.description?.toLowerCase().includes(q) ||
                item.url?.toLowerCase().includes(q),
        );
    }, [webhooks, filterText]);

    const openAdd = () => {
        setEditingItem(null);
        setShowSidebar(true);
        navigate("/webhooks/0");
    };

    const openEdit = () => {
        if (selectedRows.length !== 1) return;
        setEditingItem(selectedRows[0]);
        setShowSidebar(true);
        navigate(`/webhooks/${selectedRows[0].id}`);
    };

    const closeSidebar = () => {
        setShowSidebar(false);
        setEditingItem(null);
        navigate("/webhooks");
    };

    const handleSave = async () => {
        await refetch();
        closeSidebar();
    };

    const handleDeleteFromSidebar = async (item) => {
        await deleteOneWebhook({ variables: { where: { id: item.id } } });
        await refetch();
        closeSidebar();
    };

    const handleBulkDelete = async () => {
        setIsDeleting(true);
        setDeleteDone(0);
        try {
            for (const webhook of selectedRows) {
                await deleteOneWebhook({ variables: { where: { id: webhook.id } } });
                setDeleteDone((n) => n + 1);
            }
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err?.message || err);
        } finally {
            setIsDeleting(false);
            setDeleteDone(0);
        }
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <div
                    className="d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                        setEditingItem(row);
                        setShowSidebar(true);
                        navigate(`/webhooks/${row.id}`);
                    }}
                >
                    <span className="w-30px h-30px me-2 d-flex align-items-center justify-content-center rounded-circle border bg-color-f2f2f2">
                        <i className="fa fa-link" />
                    </span>
                    <span>{row.name}</span>
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
        },
        {
            name: "Method",
            selector: (row) => row.method,
            sortable: true,
            width: "100px",
        },
        {
            name: "Type",
            selector: (row) => row.parameters?.api_type ?? "REST",
            sortable: true,
            width: "110px",
            cell: (row) => {
                const t = row.parameters?.api_type ?? "REST";
                return t === "GRAPHQL"
                    ? <span className="badge" style={{ backgroundColor: "#e10098", color: "#fff" }}>GraphQL</span>
                    : <span className="badge bg-info text-white">REST</span>;
            },
        },
        {
            name: "URL",
            selector: (row) => row.url,
            sortable: true,
            cell: (row) => (
                <span
                    title={row.url}
                    style={{
                        maxWidth: 300,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                    }}
                >
                    {row.url}
                </span>
            ),
        },
        {
            name: "Active",
            selector: (row) => row.is_active,
            sortable: true,
            width: "90px",
            cell: (row) => (
                <span className={`badge bg-${row.is_active ? "success" : "secondary"}`}>
                    {row.is_active ? "Yes" : "No"}
                </span>
            ),
        },
        {
            name: "Created",
            selector: (row) => moment.utc(row.created_at).format("YYYY-MM-DD HH:mm"),
            sortable: true,
            width: "160px",
        },
    ];

    const customStyles = { headCells: { style: { fontWeight: "bold" } } };

    if (loading) return <EntityLoadingState icon="fa-link" />;

    if (error) {
        return (
            <div className="tw-text-center tw-mt-10 tw-text-red-600">
                Failed to load webhooks: {error.message}
            </div>
        );
    }

    return (
        <div className="container-fluid">
            <EntityOperationBar
                entity={{ singular: "Webhook", plural: "Webhooks" }}
                allowSearch
                allowAdd
                allowDelete
                allowEdit
                defaultView={viewMode}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                deleteDone={deleteDone}
                onSearch={(text) => setFilterText(text)}
                onAdd={openAdd}
                onEdit={openEdit}
                onDelete={handleBulkDelete}
                onViewChange={(mode) => {
                    setViewMode(mode);
                    setSearchParams({ view: mode });
                }}
            >
                <EntityOperationBar.View mode="card" icon="fa-th" tooltip="Card View">
                    <WebhooksCardView
                        items={webhooks}
                        selectedRows={selectedRows}
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        filterText={filterText}
                    />
                </EntityOperationBar.View>
                <EntityOperationBar.View mode="list" icon="fa-list-ul" tooltip="List View">
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) => setSelectedRows(selectedRows)}
                        clearSelectedRows={toggleCleared}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        customStyles={customStyles}
                        noDataComponent={<WebhooksEmptyState isFiltered={!!filterText.trim()} />}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <WebhookSidebar
                show={showSidebar}
                item={editingItem}
                onSave={handleSave}
                onDelete={handleDeleteFromSidebar}
                onDiscard={closeSidebar}
            />
        </div>
    );
};

export { Webhooks };
